<template>
  <div class="find-vacancy-q-page  container">
    <div v-if="pending"
         style="min-height: 20rem; position: relative">
      <BasePreloader/>
    </div>
    <ErrorView v-else-if="error"
               :code="error.code"
               :title="error.message"
               class="container-inner"/>
    <div v-else-if="steps"
         class="find-vacancy-q-page__content  container-inner">
      <h1 class="find-vacancy-q-page__title">{{ $tFindVacancy('Find a job') }}</h1>
      <div class="find-vacancy-q-page__main-content">
        <div class="find-vacancy-q-page__main">
          <BasePreloader v-if="fromPending"/>
          <FindVacancyQPagePagination :steps="stepsToShowInPagination"
                                      :currentStep="currentStep"
                                      class="find-vacancy-q-page__pagination"/>

          <h3 class="find-vacancy-q-page__step-title">
            {{ steps[currentStep].question_name }}
          </h3>
          <p v-if="steps[currentStep].question_type === 'select_many'"
             class="find-vacancy-q-page__step-title-note">
            {{ $tFindVacancy('(You can select many)') }}
          </p>

          <div class="find-vacancy-q-page__step">
            <div v-if="steps[currentStep].question_type === 'select_many'"
                 class="find-vacancy-q-page__list">
              <BaseCheckbox v-for="(variant, variantIndex) in steps[currentStep].variants"
                            :key="variantIndex + ' - '  + steps[currentStep].id"
                            :label="variant"
                            :checked="isCheckboxChecked(variant)"
                            @input="onCheckboxInput($event, variant)"/>
            </div>


            <BaseRadioList v-if="steps[currentStep].question_type === 'select_one'"
                           :key="steps[currentStep].id"
                           :options="steps[currentStep].variants"
                           v-model="stepsResults[currentStep]"
                           class="find-vacancy-q-page__list">
              <template v-slot:mark>
                <BaseSvg name="ico-check"/>
              </template>
            </BaseRadioList>

            <template v-if="isNamePhoneAnswer">
              <div class="find-vacancy-q-page__inputs">
                <BaseInput :label="isEmployer ? $tFindVacancy('Your name') : $tFindVacancy('Name')"
                           v-model="inputs.name.value"
                           :error="inputs.name.error"
                           class="find-vacancy-q-page__input"/>
                <BaseInputPhone :label="isEmployer ? $tFindVacancy('Your phone') : $tFindVacancy('Phone')"
                                v-model="inputs.phone.value"
                                :error="inputs.phone.error"
                                @validate="phoneObject = $event"
                                required
                                class="find-vacancy-q-page__input"/>
              </div>
            </template>
          </div>

          <BaseAlert :value="fromError"
                     class="find-vacancy-q-page__error  _warning"/>

          <FindVacancyQPageFooter :canNext="canNext"
                                  :canBack="canBack"
                                  :canSave="canSave"
                                  :showSave="showSave"
                                  :forAgent="isAgent || isPartner"
                                  @next="onNext"
                                  @back="onBack"
                                  @save="onSave"/>
        </div>

        <FindVacancyQPageSide/>
      </div>
    </div>
  </div>
</template>

<script>
  import {http} from '@/axios'
  import ErrorView from '@/components/ErrorView'
  import FindVacancyQPagePagination from '@/components/findVacancyQPage/FindVacancyQPagePagination'
  import FindVacancyQPageFooter from '@/components/findVacancyQPage/FindVacancyQPageFooter'
  import FindVacancyQPageSide from '@/components/findVacancyQPage/FindVacancyQPageSide'
  import handleError from '@/helpers/handleError'
  import {mapState, mapGetters} from "vuex"

  export default {
    name: 'FindVacancyQuestionnairePage',
    components: {
      ErrorView,
      FindVacancyQPagePagination,
      FindVacancyQPageFooter,
      FindVacancyQPageSide,
      // FindVacancyQPageRadioList,
    },
    data() {
      return {
        error: null,
        pending: false,
        stepsOriginal: null,
        steps: null,
        currentStep: 0,
        stepsResults: [],

        inputs: {
          name: {
            value: null
          },
          phone: {
            value: null
          }
        },

        phoneObject: null,

        fromError: null,
        fromPending: false
      }
    },
    computed: {
      ...mapState('user', [
        'profile',
      ]),
      ...mapGetters('user', [
        'isEmployer',
        'isEmployee',
        'isAgent',
        'isPartner'
      ]),
      havePoland() {// crutch for checking is poland selected in first step
        return !this.stepsResults[0]
            ? true
            : this.stepsResults[0].some(el => el === this.steps[0].variants[1])
      },
      canNextWithoutNamePhone() {
        return this.isEmployee
            && this.profile.firstname
            && this.profile.phone
      },
      canNext() {
        return !!(this.currentStep < this.steps.length - 1
            && this.stepsResults[this.currentStep])
      },
      canBack() {
        return this.currentStep > 0
      },
      canSave() {
        if (this.canNextWithoutNamePhone && this.canNext) {
          return true
        }
        return this.$get(this.phoneObject, 'isValid')
      },
      showSave() {
        let stepsLength = this.steps.length

        if (this.canNextWithoutNamePhone) {
          stepsLength -= 1
        }

        return this.currentStep === stepsLength - 1
      },
      isNamePhoneAnswer() {
        return this.steps && this.steps[this.currentStep].question_type === 'name_phone_answer'
      },
      stepsToShowInPagination() {
        if (this.canNextWithoutNamePhone) {
          return this.steps.slice(0, -1)
        }
        return this.steps
      }
    },
    methods: {
      load() {
        this.pending = true

        return http.get('/api/helper/find-vacancy/questions')
            .then(({data}) => {
              this.stepsOriginal = data.data
              this.steps = data.data
            }).finally(() => {
              this.pending = false
            })
      },
      onNext() {
        this.currentStep++
      },
      onBack() {
        this.fromError = null
        this.currentStep--
      },
      onSave() {
        this.fromPending = true
        this.fromError = null

        const sendData = {}

        sendData.result = this.steps.map((step, index) => {
          const stepResult = this.stepsResults[index]
          const item = {
            id: step.id,
            question_name: step.question_name,
            question_type: step.question_type,
          }

          if (step.question_type === 'name_phone_answer') {
            item.value = {
              name: this.inputs.name.value,
              phone: this.inputs.phone.value,
            }
          } else {
            item.value = stepResult
          }

          return item
        })

        const link = this.isAgent || this.isPartner
            ? '/api/helper/find-vacancy/send-link'
            : '/api/helper/find-vacancy/questions'

        http.post(link, sendData)
            .then(({data}) => {
              if (this.isAgent || this.isPartner) {
                this.$vModal.open('successfully-sent', {
                  title: this.$tFindVacancy('successfully_sent_title'),
                  text: this.$tFindVacancy('successfully_sent_message')
                })

                this.$router.push({
                  name: this.isAgent ? 'account-agent-resumes-base-resumes' : 'account-partner-resumes-base-resumes'
                })
              }

              this.$router.push(data.redirect_urldata || data.data.redirect_url)
            })
            .catch(e => {
              this.fromError = handleError(e)
            })
            .finally(() => {
              this.fromPending = false
            })
      },

      isCheckboxChecked(variant) {
        const currentValue = this.stepsResults[this.currentStep]

        return Array.isArray(currentValue) && currentValue.includes(variant)
      },
      onCheckboxInput(add, variant) {
        if (add) {
          if (!Array.isArray(this.stepsResults[this.currentStep])) {
            this.$set(this.stepsResults, this.currentStep, [])
          }

          this.stepsResults[this.currentStep].push(variant)
        } else {
          const cell = this.stepsResults[this.currentStep]
          if (!Array.isArray(cell)) return

          const index = cell.indexOf(variant)
          if (index < 0) return

          this.$delete(cell, index)

          if (!cell.length) {
            this.$set(this.stepsResults, this.currentStep, null)
          }
        }
      },
      autoCompleteNamePhoneStep() {
        if (!this.isEmployee) return

        this.inputs.name.value = this.profile.firstname
        this.inputs.phone.value = this.profile.phone
      }
    },
    watch: {
      currentStep(current, prev) { // remove step 6 if Poland not selected
        if (current !== 1 || prev !== 0) return

        const stepIndexToRemove = 5

        if (this.havePoland) {
          this.steps = this.stepsOriginal
          if (this.stepsResults.length > stepIndexToRemove + 1) {
            this.stepsResults.splice(stepIndexToRemove, 0, null);
          }
        } else {
          const steps = [...this.stepsOriginal]
          steps.splice(stepIndexToRemove, 1)
          this.steps = steps
          this.stepsResults.splice(stepIndexToRemove, 1)
        }
      }
    },
    async created() {
      await this.load()

      this.autoCompleteNamePhoneStep()
    },
  }
</script>

<style lang="less">

</style>
